import React, { useCallback, useLayoutEffect } from 'react';
import { ScrollView, View } from 'react-native';
import Choices from 'design-system/Choices';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { OnboardingFlow } from 'utils/types/navigation';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { useCommonInvestStyles } from './styles';
import { selectAnnualIncome } from '../reducers/selectors';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import InvestHeaderWithHelpButton from '../components/InvestHeaderWithHelpButton';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
export const CHOICES_MAP = {
    '£0 - £20,000': { liquidNetWorthMin: 0, liquidNetWorthMax: 20000 },
    '£20,000 - £49,999': { liquidNetWorthMin: 20000, liquidNetWorthMax: 49999 },
    '£50,000 - £99,999': { liquidNetWorthMin: 50000, liquidNetWorthMax: 99999 },
    '£100,000 - £499,999': { liquidNetWorthMin: 100000, liquidNetWorthMax: 499999 },
    '> £500,000': { liquidNetWorthMin: 500000, liquidNetWorthMax: 10000000 },
};
export const CHOICES = Object.keys(CHOICES_MAP);
const NetWorthScreen = ({ navigation: { setOptions, popToTop }, route: { params }, }) => {
    const colors = useColors();
    const annualIncomes = useAppSelector(selectAnnualIncome);
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    const dispatch = useAppDispatch();
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenFromPots = useNavigateToNextScreenForPotsOnboarding();
    useAmplitudeOnMountEvent(params?.flow, !!params?.mountedOnAnotherScreen, 'User Assets Screen');
    useLayoutEffect(() => {
        setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => (<HelpScreenButton articlesKey={params?.flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
        });
    }, [colors.text.primary, params?.flow, setOptions]);
    const onSubmit = useCallback(async (netWorthChoiceKey) => {
        if (annualIncomes) {
            const newParams = {
                annualIncomeMin: annualIncomes.annualIncomeMin,
                annualIncomeMax: annualIncomes.annualIncomeMax,
                ...CHOICES_MAP[netWorthChoiceKey],
            };
            dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestFundingSource', params: newParams });
            if (params?.flow === OnboardingFlow.Pots || params?.flow === OnboardingFlow.Wallet) {
                navigateToNextScreenFromPots();
            }
            else {
                navigateToNextScreen();
            }
        }
        else {
            Sentry.logError({
                filename: 'InvestNetWorth',
                message: 'User not able to go ahead',
            });
            popToTop();
        }
    }, [annualIncomes, navigateToNextScreen, navigateToNextScreenFromPots, params?.flow]);
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <InvestHeaderWithHelpButton hideButton={!params?.mountedOnAnotherScreen} isPotsOnb={params?.flow === OnboardingFlow.Pots} leftContent={params?.mountedOnAnotherScreen ? <LeaveOnboardingButton flow={params.flow}/> : undefined}/>
      <ScrollView scrollEnabled={false} contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={onboardingTitleStrings.NetWorth.title} subTitle={onboardingTitleStrings.NetWorth.subtitle}/>
        <View style={commonStyles.withPadding}>
          <Choices numbers data={CHOICES} onSelect={onSubmit}/>
        </View>
      </ScrollView>
    </View>);
};
export default NetWorthScreen;
